import React, { useState, useEffect } from 'react';
import PortalModal from '../../../common/PortalModal/PortalModal';

export default function RoleFormModal({ role, onSave, onClose }) {
  const [formData, setFormData] = useState({
    role_name: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (role) {
      setFormData({
        role_name: role.role_name || '',
        description: role.description || ''
      });
    }
  }, [role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.role_name.trim()) {
      setError('Role name is required');
      return;
    }

    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // If editing, include the role_id from the original role
      const dataToSave = role ? { ...formData, role_id: role.role_id } : formData;
      await onSave(dataToSave);
    } catch (err) {
      setError(err.message || 'Failed to save role');
      setSaving(false);
    }
  };

  return (
    <PortalModal
      isOpen
      onClose={onClose}
      title={
        <>
          <i className="fas fa-user-tag"></i>
          {role ? 'Edit Role' : 'Create New Role'}
        </>
      }
      size="medium"
      bodyClassName="rbac-modal-body"
      footer={
        <>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            form="role-form"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                {role ? 'Update Role' : 'Create Role'}
              </>
            )}
          </button>
        </>
      }
    >
      <form id="role-form" className="modal-body" onSubmit={handleSubmit}>
        <div className="form-content">
          {error && (
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              marginBottom: '1rem',
              color: '#ef4444'
            }}>
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label>
                Role Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.role_name}
                onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                placeholder="e.g., Content Editor"
                required
                disabled={saving}
              />
              <small>Display name for the role</small>
            </div>

            <div className="form-group">
              <label>
                Description <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the purpose of this role..."
                rows={4}
                required
                disabled={saving}
              />
              <small>Brief description of what this role allows</small>
            </div>
          </div>

          {!role && (
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(100, 181, 246, 0.1)', 
              border: '1px solid rgba(100, 181, 246, 0.2)',
              borderRadius: '8px',
              marginTop: '1rem',
              fontSize: '0.9rem',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              <i className="fas fa-info-circle" style={{ color: '#64b5f6' }}></i> A unique role ID will be auto-generated. After creating the role, you can attach policies to define permissions.
            </div>
          )}
        </div>
      </form>
    </PortalModal>
  );
}
